// src/components/Header.jsx
// Brutalist header component
import { useState } from 'react';
import { Bars3Icon, UserCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from './AuthWrapper';
import { useImpersonation } from '../context/ImpersonationContext';

const Header = ({ sidebarOpen, setSidebarOpen }) => {
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [endingImpersonation, setEndingImpersonation] = useState(false);
    const { user } = useAuth();
    const { isImpersonating, impersonationInfo, endImpersonation } = useImpersonation();

    const handleEndImpersonation = async () => {
        setEndingImpersonation(true);
        const result = await endImpersonation();
        if (!result.success) {
            alert('Failed to end impersonation: ' + result.error);
        }
        setEndingImpersonation(false);
    };

    return (
        <>
            {/* Impersonation Banner - High contrast warning */}
            {isImpersonating && (
                <div className="bg-amber-500 text-black px-4 py-2">
                    <div className="flex items-center justify-between font-mono text-xs">
                        <div className="flex items-center gap-3">
                            <ExclamationTriangleIcon className="h-4 w-4" />
                            <span className="font-bold uppercase">
                                IMPERSONATING: {impersonationInfo?.impersonatedUser?.email}
                            </span>
                            <span className="text-amber-800">
                                (Admin: {impersonationInfo?.impersonatedBy?.email})
                            </span>
                        </div>
                        <button
                            onClick={handleEndImpersonation}
                            disabled={endingImpersonation}
                            className="flex items-center gap-1 px-3 py-1 bg-black text-amber-500 font-bold hover:bg-gray-900 disabled:opacity-50"
                        >
                            <XMarkIcon className="h-4 w-4" />
                            <span>{endingImpersonation ? 'ENDING...' : 'END'}</span>
                        </button>
                    </div>
                </div>
            )}

            <header className="sticky top-0 z-20 bg-surface/70 backdrop-blur-xl border-b border-line">
                <div className="flex items-center justify-between h-14 px-4">
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            className="p-2 text-gray-500 hover:text-white md:hidden"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        >
                            <Bars3Icon className="h-5 w-5" />
                        </button>
                        <h1 className="text-sm font-mono font-bold text-white uppercase tracking-wide">
                            Admin Console
                            {isImpersonating && (
                                <span className="ml-2 text-amber-700 font-normal">
                                    [IMPERSONATING]
                                </span>
                            )}
                        </h1>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* User menu */}
                        <div className="relative">
                            <button
                                type="button"
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-md font-mono text-xs border transition-all duration-150 ${
                                    isImpersonating
                                        ? 'text-amber-700 border-amber-500/30 hover:bg-amber-500/10'
                                        : 'text-gray-300 border-line hover:bg-slate-100 hover:text-white hover:border-line-strong'
                                }`}
                                onClick={() => setUserMenuOpen(!userMenuOpen)}
                            >
                                <UserCircleIcon className="h-4 w-4" />
                                <span className="uppercase">
                                    {user?.firstName || user?.email?.split('@')[0] || 'ADMIN'}
                                </span>
                            </button>

                            {userMenuOpen && (
                                <div className="absolute right-0 mt-2 w-56 bg-surface-2 border border-line rounded-lg shadow-elevated z-50 overflow-hidden animate-scale-in origin-top-right">
                                    <div className="px-4 py-3 border-b border-line">
                                        <p className="font-mono text-xs text-white font-bold uppercase">
                                            {user?.firstName} {user?.lastName}
                                        </p>
                                        <p className="font-mono text-xs text-gray-500 mt-1">
                                            {user?.email}
                                        </p>
                                        <p className="font-mono text-xs text-primary mt-1">
                                            {user?.role}
                                        </p>
                                    </div>
                                    {isImpersonating && (
                                        <button
                                            onClick={handleEndImpersonation}
                                            disabled={endingImpersonation}
                                            className="block w-full text-left px-4 py-2 font-mono text-xs text-amber-700 hover:bg-slate-100 disabled:opacity-50 uppercase"
                                        >
                                            {endingImpersonation ? 'ENDING...' : 'END IMPERSONATION'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setUserMenuOpen(false)}
                                        className="block w-full text-left px-4 py-2 font-mono text-xs text-gray-400 hover:bg-slate-100 hover:text-white uppercase"
                                    >
                                        PROFILE
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Click outside to close user menu */}
                {userMenuOpen && (
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setUserMenuOpen(false)}
                    />
                )}
            </header>
        </>
    );
};

export default Header;
